import React, { useState, useEffect } from 'react';
import {
  useEditorState,
  getMarks,
  setMarks,
  focusEditor,
  useEventPlateId,
} from '@udecode/plate-common';
import clsx from 'clsx';

import { getGoogleFonts, GOOGLE_FONT_LINK_ID } from 'components/PlateEditor/plugins/fonts/createFontFamilyPlugin';
import ToolbarDropdown, { ToolbarDropdownButton } from './ToolbarDropdown';

import './font-dropdown.scss';

export const FONT_OPEN_SANS = 'Open Sans';
export const FONT_MONTSERRAT = 'Montserrat';
export const FONT_PLAYFAIR = 'Playfair Display';
export const FONT_ARIAL = 'Arial';
export const FONT_CALIBRI = 'Calibri';
export const FONT_CAMBRIA = 'Cambria';
export const FONT_COMFORTAA = 'Comfortaa';
export const FONT_COURIER_NEW = 'Courier New';
export const FONT_DANCING_SCRIPT = 'Dancing Script';
export const FONT_EB_GARAMOND = 'EB Garamond';
export const FONT_GEORGIA = 'Georgia';
export const FONT_HELVETICA = 'Helvetica';
export const FONT_LEXEND = 'Lexend';
export const FONT_ROBOTO = 'Roboto';
export const FONT_SPECTRAL = 'Spectral';
export const FONT_SYNCOPATE = 'Syncopate';
export const FONT_TIMES = 'Times New Roman';
export const FONT_VERDANA = 'Verdana';


export const fonts = [
  FONT_ARIAL,
  FONT_CALIBRI,
  FONT_CAMBRIA,
  FONT_COMFORTAA,
  FONT_COURIER_NEW,
  FONT_DANCING_SCRIPT,
  FONT_EB_GARAMOND,
  FONT_GEORGIA,
  FONT_HELVETICA,
  FONT_LEXEND,
  FONT_MONTSERRAT,
  FONT_OPEN_SANS,
  FONT_PLAYFAIR,
  FONT_ROBOTO,
  FONT_SPECTRAL,
  FONT_SYNCOPATE,
  FONT_TIMES,
  FONT_VERDANA,
];

export const italicFonts = [
  FONT_ARIAL,
  FONT_CALIBRI,
  FONT_CAMBRIA,
  FONT_COURIER_NEW,
  FONT_EB_GARAMOND,
  FONT_GEORGIA,
  FONT_HELVETICA,
  FONT_MONTSERRAT,
  FONT_OPEN_SANS,
  FONT_PLAYFAIR,
  FONT_ROBOTO,
  FONT_TIMES,
  FONT_VERDANA,
];

const DEFAULT_FONT = FONT_MONTSERRAT;

const FontDropdown = ({ id, ...props }) => {
  const [open, setOpen] = useState(false);
  const editor = useEditorState(useEventPlateId(id));
  const marks = getMarks(editor);
  const { fontFamily } = marks || { fontFamily: DEFAULT_FONT };

  const setFont = (e, value) => {
    e.preventDefault();
    e.stopPropagation();
    setMarks(editor, { fontFamily: value });
    setTimeout(() => {
      focusEditor(editor);
      setOpen(false);
    }, 0);
  };

  const show = () => {
    setOpen(true);
  };

  const dismiss = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!document.getElementById(GOOGLE_FONT_LINK_ID)) {
      getGoogleFonts();
    }
  }, []);

  const trigger = (
    <ToolbarDropdownButton
      className="toolbar-dropdown__button"
      showIcon
      {...props}
    >
      {fontFamily ?? DEFAULT_FONT }
    </ToolbarDropdownButton>
  );

  return (
    <ToolbarDropdown
      isOpen={open}
      show={show}
      dismiss={dismiss}
      DropdownButton={trigger}
      className="plate-toolbar__dropdown toolbar-dropdown font-dropdown"
      tooltipName="Change font"
      root={null}
    >
      {fonts.map(font => (
        <FontItem
          key={font}
          font={font}
          setFont={setFont}
          active={fontFamily === font}
        />
      ))}
    </ToolbarDropdown>
  );
};

const FontItem = ({ font, setFont, active }) => {
  return (
    <button
      key={`font-button--${font}`}
      className={clsx('button--unstyled', 'font-dropdown__item', active && 'active')}
      onClick={(e) => setFont(e, font)}
      onMouseDown={(e) => e.preventDefault()}
      style={{ fontFamily: font }}
    >
      {font}
    </button>
  );
};

export default FontDropdown;
